import React, { useState, useEffect } from 'react';
import { X, Save, Upload, Plus, Trash2, Layout, Box, FileText, UserPlus, Search, Calendar, Clock, User, Home, ChevronRight, ChevronLeft, Check, Download, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import surveyService from '../services/surveyService';
import { clientService, userService, appointmentService, settingsService } from '../services/api';
import projectService from '../services/projectsService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAvailableTimeSlots } from '../hooks/useAvailableTimeSlots';
import FileUpload from './FileUpload';
import SketchEditor from './SketchEditor';
import SurveyEnvironments from './SurveyEnvironments';
import toast from 'react-hot-toast';

// Função para obter a data atual no fuso horário local
const getLocalDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

const STEPS = [
  { id: 1, title: 'Cliente', icon: User },
  { id: 2, title: 'Técnico', icon: FileText },
  { id: 3, title: 'Materiais', icon: Home },
  { id: 4, title: 'Arquivos', icon: Upload },
  { id: 5, title: 'Revisão', icon: Check }
];

const Stepper = ({ steps, currentStep, onStepClick }) => (
  <div className="flex items-center justify-center mb-4 sm:mb-6 overflow-x-auto px-1">
    {steps.map((step, index) => {
      const Icon = step.icon;
      const isActive = currentStep === step.id;
      const isCompleted = currentStep > step.id;
      return (
        <React.Fragment key={step.id}>
          <button
            type="button"
            onClick={() => isCompleted && onStepClick(step.id)}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2 rounded-lg transition-all flex-shrink-0 ${isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-400'}`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-medium hidden sm:inline">{step.title}</span>
          </button>
          {index < steps.length - 1 && <div className={`w-4 sm:w-6 h-0.5 mx-0.5 sm:mx-1 ${isCompleted ? 'bg-green-400' : 'bg-gray-300'}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

const SurveyForm = ({ survey, onClose, onSuccess }) => {
  const { user } = useAuth();
  // Verificar se é edição apenas se survey tem ID válido (survey existente no banco)
  // Se survey tem dados mas sem ID, é um novo survey com dados pré-preenchidos (vindo do appointment)
  const isEditing = !!(survey && survey.id);
  const canViewCommercial = user.role === 'GERENCIA';
  const isRPEditing3D = user.role === 'RECEPCAO_PROJETOS' && isEditing;

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.client_name?.trim()) { toast.error('Nome do cliente é obrigatório'); return false; }
      if (!formData.client_address?.trim()) { toast.error('Endereço é obrigatório'); return false; }
    }
    if (step === 2) {
      if (!formData.environment_description?.trim()) { toast.error('Descrição do ambiente é obrigatória'); return false; }
    }
    return true;
  };

  const saveSurveyForId = async () => {
    if (savedSurveyId || (survey && survey.id)) {
      return savedSurveyId || survey.id;
    }
    try {
      const response = await surveyService.createSurvey(formData);
      const newId = response.data.id;
      setSavedSurveyId(newId);
      toast.success('Levantamento salvo! Agora você pode enviar arquivos.');
      return newId;
    } catch (err) {
      toast.error('Erro ao salvar levantamento');
      return null;
    }
  };

  const handleNextStep = async () => {
    if (!validateStep(currentStep)) return;

    if (currentStep === 3 && !savedSurveyId && !(survey && survey.id)) {
      const newId = await saveSurveyForId();
      if (!newId) return;
    }

    setCurrentStep(s => Math.min(5, s + 1));
  };

  const handleMaterialNotCollected = () => {
    const newState = !materialNotCollected;
    setMaterialNotCollected(newState);
    if (newState) {
      setFormData(prev => ({
        ...prev,
        material_preferences: {
          material_type: [],
          hardware_type: [],
          has_glass: false,
          door_finish: [],
          not_collected: true
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        material_preferences: {
          ...prev.material_preferences,
          not_collected: false
        }
      }));
    }
  };

  const handleStart3DProject = async () => {
    if (!savedSurveyId && !(survey && survey.id)) {
      toast.error('Salve o levantamento primeiro');
      return;
    }

    const deadlineStr = window.prompt(
      'Digite o prazo de entrega do projeto 3D (formato: YYYY-MM-DD HH:MM):',
      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16).replace('T', ' ')
    );

    if (!deadlineStr) {
      return;
    }

    const deadlineDate = new Date(deadlineStr.replace(' ', 'T'));
    if (isNaN(deadlineDate.getTime()) || deadlineDate <= new Date()) {
      toast.error('Data/hora inválida ou no passado. Use formato: YYYY-MM-DD HH:MM');
      return;
    }

    const surveyId = savedSurveyId || survey.id;

    try {
      const updateData = {
        status: 'EM_PROJETACAO',
        project_3d_status: 'IN_PROGRESS',
        project_3d_deadline: deadlineDate.toISOString()
      };

      await surveyService.updateSurvey(surveyId, updateData);

      setFormData(prev => ({ ...prev, status: 'EM_PROJETACAO' }));
      setProject3DStatus('IN_PROGRESS');

      queryClient.invalidateQueries(['survey', surveyId]);
      queryClient.invalidateQueries(['surveys']);

      toast.success('Projeto 3D iniciado! Vá para a aba de Croqui 2D para adicionar os arquivos.');
    } catch (err) {
      console.error('Erro ao iniciar projeto 3D:', err);
      toast.error(err.response?.data?.message || 'Erro ao iniciar projeto 3D');
    }
  };

  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_apelido: '',
    client_phone: '',
    client_email: '',
    client_address: '',
    environment_description: '',
    technical_observations: '',
    plumbing_info: '',
    furniture_purpose: '',
    material_preferences: {
      material_type: [],
      hardware_type: [],
      has_glass: false,
      door_finish: []
    },
    client_profile: {
      demanding: false,
      existing_quality: '',
      behavior_notes: ''
    },
    estimated_budget_range: '',
    status: 'RASCUNHO',
    return_reason: '',
    priority: 'NORMAL',
    survey_date: '',
    project_deadline: ''
  });

  const [errors, setErrors] = useState({});
  const [savedSurveyId, setSavedSurveyId] = useState(survey?.id || null);
  const [files, setFiles] = useState(survey?.files || []);
  const [files3D, setFiles3D] = useState(survey?.files_3d || []);
  const [project3DObservations, setProject3DObservations] = useState(survey?.project_3d_observations || '');
  const [project3DStatus, setProject3DStatus] = useState(
    user.role === 'RECEPCAO_PROJETOS' && (!survey?.project_3d_status || survey?.project_3d_status === 'PENDING')
      ? 'IN_PROGRESS'
      : (survey?.project_3d_status || 'PENDING')
  );
  const [project3DRejectionReason, setProject3DRejectionReason] = useState(survey?.project_3d_rejection_reason || '');
  const [uploading3D, setUploading3D] = useState(false);
  const [activeTab, setActiveTab] = useState('form'); // 'form' ou 'sketch'
  const [sketchProjectId, setSketchProjectId] = useState(survey?.sketch_project_id || null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [shouldFinalize, setShouldFinalize] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting3D, setRejecting3D] = useState(false);
  const [approving3D, setApproving3D] = useState(false);
  const [materialNotCollected, setMaterialNotCollected] = useState(false);
  const queryClient = useQueryClient();

  // Estados para apresentação
  const [selectedPresentationUser, setSelectedPresentationUser] = useState(null);
  const [selectedPresentationDate, setSelectedPresentationDate] = useState('');
  const [selectedPresentationTime, setSelectedPresentationTime] = useState('');
  const [presentationNotes, setPresentationNotes] = useState('');
  const [creatingPresentation, setCreatingPresentation] = useState(false);

  // Estados para criação rápida de cliente
  const [newClientData, setNewClientData] = useState({
    full_name: '',
    apelido: '',
    email: '',
    phone: '',
    document_id: '',
    address: ''
  });

  // Estados separados para endereço no modal de cliente
  const [newClientAddressFields, setNewClientAddressFields] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const [loadingCepNewClient, setLoadingCepNewClient] = useState(false);

  // Query para buscar clientes
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['clients', { search: clientSearchTerm }],
    queryFn: () => clientService.getClients({
      search: clientSearchTerm || undefined,
      limit: 50
    }),
    enabled: true
  });

  // Normalizar clientes
  const clients = clientsData?.data?.data || clientsData?.data || [];

  // Mutation para criar cliente
  const createClientMutation = useMutation({
    mutationFn: (data) => clientService.createClient(data),
    onSuccess: (response) => {
      toast.success('Cliente criado com sucesso!');
      queryClient.invalidateQueries(['clients']);
      // Preencher o formulário com o novo cliente
      const newClient = response.data?.client || response.data;
      if (newClient) {
        handleClientSelect(newClient.id);
      }
      setShowClientModal(false);
      setNewClientData({
        full_name: '',
        apelido: '',
        email: '',
        phone: '',
        document_id: '',
        address: ''
      });
      setNewClientAddressFields({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar cliente');
    }
  });

  // Função para quando selecionar um cliente
  const handleClientSelect = async (clientId) => {
    let selectedClient = clients.find(c => c.id === parseInt(clientId));
    
    // Se não encontrou na lista atual, buscar da API
    if (!selectedClient && clientId) {
      try {
        const response = await clientService.getClient(clientId);
        selectedClient = response.data?.client || response.data;
      } catch (error) {
        console.error('Erro ao buscar cliente:', error);
        toast.error('Erro ao carregar dados do cliente');
        return;
      }
    }
    
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        client_id: selectedClient.id,
        client_name: selectedClient.full_name || '',
        client_apelido: selectedClient.apelido || '',
        client_phone: selectedClient.phone || '',
        client_email: selectedClient.email || '',
        client_address: selectedClient.address || ''
      }));
      setClientSearchTerm(''); // Limpar busca
    }
  };

  // Debug survey prop
  useEffect(() => {
    console.log('🔍 SurveyForm received survey prop:', survey);
  }, [survey]);

  useEffect(() => {
    if (survey) {
      console.log('🔍 SurveyForm useEffect triggered, survey:', survey);
      // Função auxiliar para garantir que valores de string nunca sejam null
      const ensureString = (value) => (value === null || value === undefined ? '' : String(value));

      setFormData({
        ...survey,
        // Garantir que todos os campos de string sejam strings vazias ao invés de null
        client_id: survey.client_id ? String(survey.client_id) : '',
        client_name: ensureString(survey.client_name),
        client_apelido: ensureString(survey.client_apelido),
        client_phone: ensureString(survey.client_phone),
        client_email: ensureString(survey.client_email),
        client_address: ensureString(survey.client_address),
        environment_description: ensureString(survey.environment_description),
        technical_observations: ensureString(survey.technical_observations),
        plumbing_info: ensureString(survey.plumbing_info),
        furniture_purpose: ensureString(survey.furniture_purpose),
        material_preferences: survey.material_preferences || {
          material_type: [],
          hardware_type: [],
          has_glass: false,
          door_finish: []
        },
        client_profile: {
          demanding: survey.client_profile?.demanding || false,
          existing_quality: ensureString(survey.client_profile?.existing_quality),
          behavior_notes: ensureString(survey.client_profile?.behavior_notes)
        },
        estimated_budget_range: ensureString(survey.estimated_budget_range),
        status: survey.status || 'RASCUNHO',
        priority: survey.priority || 'NORMAL',
        survey_date: survey.survey_date ? survey.survey_date.split('T')[0] : '',
        project_deadline: survey.project_deadline ? survey.project_deadline.split('T')[0] : '',
        return_reason: ensureString(survey.return_reason)
      });
      setSketchProjectId(survey.sketch_project_id || null);
      setFiles(survey.files || []);
      setFiles3D(survey.files_3d || []);
      setProject3DObservations(survey.project_3d_observations || '');
      setProject3DStatus(
        user.role === 'RECEPCAO_PROJETOS' && (!survey.project_3d_status || survey.project_3d_status === 'PENDING')
          ? 'IN_PROGRESS'
          : (survey.project_3d_status || 'PENDING')
      );
      setProject3DRejectionReason(survey.project_3d_rejection_reason || '');
    }
  }, [survey]);

  const maskPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (!digits) return '';
    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  // Função para aplicar máscara de CPF/CNPJ
  const maskDocument = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    }
  };

  // Função para aplicar máscara de CEP
  const maskCep = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
  };

  // Função para buscar CEP via API ViaCEP
  const fetchCepNewClient = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('CEP deve conter 8 dígitos');
      return;
    }

    setLoadingCepNewClient(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
      const data = await response.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      // Preencher campos automaticamente
      setNewClientAddressFields({
        ...newClientAddressFields,
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || ''
      });

      toast.success('CEP encontrado! Preencha o número e complemento se necessário.');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCepNewClient(false);
    }
  };

  const handleCepChangeNewClient = (e) => {
    const maskedValue = maskCep(e.target.value);
    setNewClientAddressFields({ ...newClientAddressFields, cep: maskedValue });
  };

  const handleCepBlurNewClient = () => {
    if (newClientAddressFields.cep.replace(/\D/g, '').length === 8) {
      fetchCepNewClient(newClientAddressFields.cep);
    }
  };

  const handleAddressFieldChangeNewClient = (field, value) => {
    setNewClientAddressFields({ ...newClientAddressFields, [field]: value });
  };

  // Função para construir endereço completo do novo cliente
  const buildNewClientFullAddress = () => {
    const parts = [];
    if (newClientAddressFields.street) parts.push(newClientAddressFields.street);
    if (newClientAddressFields.number) parts.push(`nº ${newClientAddressFields.number}`);
    if (newClientAddressFields.complement) parts.push(newClientAddressFields.complement);
    if (newClientAddressFields.neighborhood) parts.push(newClientAddressFields.neighborhood);
    if (newClientAddressFields.city) parts.push(newClientAddressFields.city);
    if (newClientAddressFields.state) parts.push(newClientAddressFields.state);
    if (newClientAddressFields.cep) parts.push(`CEP: ${newClientAddressFields.cep}`);
    
    return parts.join(', ');
  };

  const handleReject3D = async () => {
    try {
      setRejecting3D(true);
      await projectService.reject3DProject(savedSurveyId, rejectionReason);
      toast.success('Projeto 3D rejeitado. Levantamento retornou para EM_PROJETACAO.');
      setShowRejectModal(false);
      setRejectionReason('');
      if (onSuccess) onSuccess();
      if (queryClient) queryClient.invalidateQueries(['surveys']);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao rejeitar projeto 3D');
    } finally {
      setRejecting3D(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('client_profile.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        client_profile: {
          ...prev.client_profile,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      let newValue = type === 'checkbox' ? checked : value;

      if (name === 'client_phone') {
        newValue = maskPhone(value);
      }

      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
    }

    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar dados
    const validation = surveyService.validateSurveyData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setLoading(true);
      
      // Log para debug
      console.log('📤 Enviando dados do formulário:', {
        status: formData.status,
        formData: formData
      });
      
      let surveyId = savedSurveyId;

      // Se tem savedSurveyId (survey já salvo no banco), atualizar
      // Se tem survey.id (modo edição tradicional), atualizar
      // Caso contrário, criar novo
      if (savedSurveyId) {
        // Se já existe um levantamento salvo anteriormente, atualizar
        console.log('💾 Atualizando levantamento salvo:', savedSurveyId, 'com status:', formData.status);
        // Incluir project_3d_status e project_3d_observations no submit
        const submitData = {
          ...formData,
          project_3d_status: project3DStatus,
          project_3d_observations: project3DObservations
        };
        const response = await surveyService.updateSurvey(savedSurveyId, submitData);
        console.log('✅ Resposta da atualização:', response);

        // Atualizar o estado do formulário com os dados retornados
        if (response.data) {
          setFormData(prev => ({
            ...prev,
            status: response.data.status || prev.status
          }));
        }

        toast.success('Levantamento atualizado com sucesso!');
        surveyId = savedSurveyId;
      } else if (survey && survey.id) {
        // Modo edição tradicional (survey com ID válido do banco)
        console.log('✏️ Atualizando levantamento:', survey.id, 'com status:', formData.status);
        // Incluir project_3d_status e project_3d_observations no submit
        const submitData = {
          ...formData,
          project_3d_status: project3DStatus,
          project_3d_observations: project3DObservations
        };
        const response = await surveyService.updateSurvey(survey.id, submitData);
        console.log('✅ Resposta da atualização:', response);

        // Atualizar o estado do formulário com os dados retornados
        if (response.data) {
          setFormData(prev => ({
            ...prev,
            status: response.data.status || prev.status
          }));
        }

        toast.success('Levantamento atualizado com sucesso!');
        surveyId = survey.id;
      } else {
        // Se não existe levantamento salvo, criar um novo
        const response = await surveyService.createSurvey(formData);
        toast.success('Levantamento criado com sucesso!');
        surveyId = response.data.id;
        setSavedSurveyId(surveyId);
        // Carregar o levantamento completo para obter o sketch_project_id
        if (response.data.sketch_project_id) {
          setSketchProjectId(response.data.sketch_project_id);
        } else {
          // Buscar o levantamento completo para obter o sketch_project_id
          try {
            const fullSurvey = await surveyService.getSurveyById(surveyId);
            if (fullSurvey.data?.sketch_project_id) {
              setSketchProjectId(fullSurvey.data.sketch_project_id);
            }
          } catch (err) {
            console.warn('Erro ao buscar sketch_project_id:', err);
          }
        }
      }

      // Invalidar cache de sessões de atendimento e levantamentos
      queryClient.invalidateQueries(['appointment-sessions-current']);
      queryClient.invalidateQueries(['appointment-sessions-today']);
      queryClient.invalidateQueries(['appointment-sessions-today-sessions']);
      queryClient.invalidateQueries(['survey']);
      queryClient.invalidateQueries(['surveys']);

      // Se estiver no modo de edição rápida de 3D (Recepção Projetos), fechar o modal
      if (isRPEditing3D) {
        toast.success(
          project3DStatus === 'COMPLETED'
            ? 'Projeto 3D concluído e atendimento finalizado com sucesso!'
            : 'Projeto 3D atualizado com sucesso!'
        );
        if (onSuccess) onSuccess();
        onClose();
        return;
      }
      
      // Se não está editando e não tem arquivos, manter o formulário aberto para upload
      if (!isEditing && !savedSurveyId && files.length === 0) {
        toast.info('Agora você pode fazer upload de arquivos e fotos!');
        return; // Não fecha o formulário
      }
      
      // Se o usuário clicou em "Salvar e Finalizar", finalizar após salvar
      if (shouldFinalize) {
        setShouldFinalize(false);
        toast.success(
          'Levantamento salvo! Agora vá em "Ambientes" para criar os ambientes e fazer o croqui 2D de cada um.',
          { duration: 8000 }
        );
        onSuccess();
        return;
      }
      
      // Se já tinha um levantamento salvo e agora atualizou, não fechar automaticamente
      // O usuário pode clicar em "Finalizar" se quiser fechar
    } catch (error) {
      console.error('Erro ao salvar levantamento:', error);
      toast.error('Erro ao salvar levantamento');
    } finally {
      setLoading(false);
    }
  };

  // Se estiver na aba de croqui e tiver um sketch_project_id, mostrar o editor
  if (activeTab === 'sketch' && sketchProjectId) {
    return (
      <SketchEditor
        projectId={sketchProjectId}
        onClose={() => setActiveTab('form')}
      />
    );
  }

  // Se estiver na aba de ambientes, mostrar o componente de gerenciamento
  if (activeTab === 'environments' && savedSurveyId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Gerenciar Ambientes
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Levantamento #{savedSurveyId}
              </p>
            </div>
            <button
              onClick={() => setActiveTab('form')}
              className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
          {/* Environments Component */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            <SurveyEnvironments
              surveyId={savedSurveyId}
              surveyStatus={formData.status}
              project3DStatus={project3DStatus}
              onStart3DProject={handleStart3DProject}
              readOnly={!isEditing}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[calc(100vh-16px)] sm:max-h-[calc(100vh-32px)] sm:h-[calc(100vh-32px)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate max-w-[120px] sm:max-w-none">
            {isRPEditing3D ? 'Editar Projeto 3D' : (isEditing ? 'Editar Levantamento' : 'Novo Levantamento')}
          </h2>
          {!isRPEditing3D && (
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Tabs */}
            <div className="flex gap-1 sm:gap-2 border border-gray-300 rounded-lg p-1 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveTab('form')}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'form'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="hidden sm:inline">Formulário</span>
                <span className="sm:hidden">Form</span>
              </button>
              {(user.role === 'GERENCIA' || user.role === 'RECEPCAO_PROJETOS') && savedSurveyId && (
                <button
                    type="button"
                    onClick={() => {
                      if (savedSurveyId) {
                        setActiveTab('environments');
                      } else {
                        toast.error('Salve o levantamento primeiro para gerenciar ambientes');
                      }
                    }}
                    disabled={!savedSurveyId}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'environments'
                        ? 'bg-blue-600 text-white'
                        : savedSurveyId
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Ambientes</span>
                  </button>
              )}
            </div>
          </div>
          )}
          <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

        {/* Form Content - scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">

            {!isRPEditing3D && (
              <Stepper steps={STEPS} currentStep={currentStep} onStepClick={setCurrentStep} />
            )}

            {/* RP mode: Show only 3D project section */}
            {isRPEditing3D && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Box className="w-5 h-5 text-purple-600" />
                  🎨 Projeto 3D
                </h3>
                <div className="space-y-4">
                  {/* Status do Projeto 3D */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status do Projeto 3D
                      </label>
                      <select
                        value={project3DStatus}
                        onChange={(e) => setProject3DStatus(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {surveyService.get3DStatusOptions(user?.role).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Observações do Projeto 3D */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações do Projeto 3D
                    </label>
                    <textarea
                      value={project3DObservations}
                      onChange={(e) => setProject3DObservations(e.target.value)}
                      rows={6}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Descreva o projeto 3D, observações técnicas, ajustes necessários, especificações importantes..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use este campo para documentar detalhes do projeto 3D, ajustes necessários, especificações técnicas, etc.
                    </p>
                  </div>

                    {/* Upload de Arquivos 3D */}
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                        <Upload className="w-4 h-4" />
                        <span className="hidden sm:inline">Arquivos do Projeto 3D</span>
                        <span className="sm:hidden">Arquivos 3D</span>
                      </h4>

                      {/* Lista de arquivos 3D existentes */}
                      {files3D.length > 0 && (
                        <div className="mb-3 sm:mb-4 space-y-2">
                          <p className="text-xs sm:text-sm font-medium text-gray-700">Arquivos enviados:</p>
                          <div className="space-y-2">
                            {files3D.map((file) => (
                              <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
                                  <span className="text-xs sm:text-sm text-gray-700 truncate">{file.original_name}</span>
                                  <span className="text-xs text-gray-500 hidden sm:inline">
                                    ({surveyService.formatFileSize(file.file_size)})
                                  </span>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const token = localStorage.getItem('accessToken');
                                      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                                      const apiPath = baseUrl.includes('/api') ? '' : '/api';
                                      window.location.href = `${baseUrl}${apiPath}/surveys/files/${file.id}/download?token=${token}`;
                                    }}
                                    className="text-green-600 hover:text-green-800 p-2"
                                    title="Baixar"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        await surveyService.deleteFile(file.id);
                                        setFiles3D(files3D.filter(f => f.id !== file.id));
                                        toast.success('Arquivo removido com sucesso');
                                      } catch (error) {
                                        toast.error('Erro ao remover arquivo');
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-800 p-2"
                                    title="Remover"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Upload de novos arquivos */}
                      <div className="border-2 border-dashed border-purple-300 rounded-lg p-3 sm:p-4">
                        <input
                          type="file"
                          multiple
                          accept=".3ds,.max,.blend,.obj,.fbx,.dae,.ply,.stl,.gltf,.glb,.skp,.dwg,.dxf,.pdf,.jpg,.jpeg,.png"
                          onChange={async (e) => {
                            const selectedFiles = Array.from(e.target.files);
                            if (selectedFiles.length === 0) return;

                            try {
                              setUploading3D(true);
                              const response = await surveyService.upload3DFiles(
                                savedSurveyId,
                                selectedFiles,
                                'Arquivos do projeto 3D'
                              );

                              setFiles3D([...files3D, ...response.data]);
                              toast.success(`${selectedFiles.length} arquivo(s) 3D enviado(s) com sucesso!`);

                              e.target.value = '';
                            } catch (error) {
                              console.error('Erro ao fazer upload:', error);
                              toast.error('Erro ao fazer upload dos arquivos 3D');
                            } finally {
                              setUploading3D(false);
                            }
                          }}
                          disabled={uploading3D || !savedSurveyId}
                          className="hidden"
                          id="upload-3d-files"
                        />
                        <label
                          htmlFor="upload-3d-files"
                          className={`flex flex-col items-center justify-center cursor-pointer ${
                            uploading3D || !savedSurveyId ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Upload className="w-10 h-10 sm:w-8 sm:h-8 text-purple-600 mb-2" />
                        <p className="text-xs sm:text-sm text-gray-700 font-medium">
                          {uploading3D ? 'Enviando...' : 'Selecionar arquivos 3D'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                          Formatos: .3ds, .max, .blend, .obj, .fbx, .dae, .ply, .stl, .gltf, .glb, .skp, .dwg, .dxf, .pdf, imagens
                        </p>
                        <p className="text-xs text-gray-400 mt-1 hidden md:block">
                          Máximo 20 arquivos, 50MB cada
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Salvar 3D */}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Salvar Projeto 3D
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 1: Cliente */}
            {!isRPEditing3D && currentStep === 1 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              {['COMPLETED', 'REVIEW', 'APPROVED'].includes(project3DStatus) && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2 text-purple-800">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="w-5 h-5"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><path d="m3.3 7 8.7 5 8.7-5"></path><path d="M12 22V12"></path></svg>
                  <span className="text-sm font-medium">Projeto 3D em análise. Dados do cliente são apenas para referência.</span>
                </div>
              )}

              {project3DStatus === 'REJECTED' && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
                    <span className="text-sm font-bold">Projeto 3D Rejeitado</span>
                  </div>
                  {project3DRejectionReason && (
                    <div className="text-sm text-red-700">
                      <span className="font-medium">Motivo da rejeição:</span>
                      <p className="mt-1 p-2 bg-white border border-red-200 rounded">{project3DRejectionReason}</p>
                    </div>
                  )}
                  <p className="text-xs text-red-600 mt-2">Faça as correções necessárias e reenvie o projeto 3D.</p>
                </div>
              )}

              {formData.status === 'AGUARDANDO_APRESENTACAO' && !formData.presentation_appointment_id && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <span className="text-sm font-bold">Aguardando Agendamento de Apresentação</span>
                  </div>
                  <p className="text-sm text-yellow-700 mb-2">
                    O projeto 3D foi aprovado. Entre em contato com o cliente e agende a apresentação.
                  </p>
                  <a
                    href={`/agendamentos/novo?survey_id=${survey?.id || savedSurveyId}&client_name=${encodeURIComponent(formData.client_name || '')}&client_phone=${encodeURIComponent(formData.client_phone || '')}&client_address=${encodeURIComponent(formData.client_address || '')}`}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" x2="16" y1="2" y2="6"></line><line x1="8" x2="8" y1="2" y2="6"></line><line x1="3" x2="21" y1="10" y2="10"></line></svg>
                    Agendar Apresentação
                  </a>
                </div>
              )}

              {formData.status === 'AGUARDANDO_APRESENTACAO' && formData.presentation_appointment_id && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="w-5 h-5"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" x2="16" y1="2" y2="6"></line><line x1="8" x2="8" y1="2" y2="6"></line><line x1="3" x2="21" y1="10" y2="10"></line></svg>
                    <span className="text-sm font-bold">Apresentação Agendada</span>
                  </div>
                  <p className="text-sm text-green-700">
                    A apresentação está agendada. Vá até <strong>/atendimentos</strong> no dia e horário marcado para realizar a apresentação ao cliente.
                  </p>
                </div>
              )}
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dados do Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Seleção de Cliente - oculta quando vem de agendamento */}
                {!survey?.fromAppointment && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selecionar Cliente *
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={clientSearchTerm}
                          onChange={(e) => setClientSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Buscar cliente por nome ou email..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowClientModal(true)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="hidden sm:inline">Novo Cliente</span>
                      </button>
                    </div>
                    {clientSearchTerm && (
                      <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg">
                        {loadingClients ? (
                          <div className="p-4 text-center text-gray-500">Carregando...</div>
                        ) : clients.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">Nenhum cliente encontrado</div>
                        ) : (
                          <ul className="divide-y divide-gray-200">
                            {clients.map((client) => (
                              <li
                                key={client.id}
                                onClick={() => {
                                  handleClientSelect(client.id);
                                  setClientSearchTerm('');
                                }}
                                className="p-3 hover:bg-blue-50 cursor-pointer transition-colors"
                              >
                                <div className="font-medium text-gray-900">{client.full_name}</div>
                                {client.email && (
                                  <div className="text-sm text-gray-500">{client.email}</div>
                                )}
                                {client.phone && (
                                  <div className="text-sm text-gray-500">{client.phone}</div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="client_name"
                    value={formData.client_name || ''}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.client_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nome completo do cliente"
                  />
                  {errors.client_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.client_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apelido
                  </label>
                  <input
                    type="text"
                    name="client_apelido"
                    value={formData.client_apelido || ''}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Como o cliente prefere ser chamado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    name="client_phone"
                    value={formData.client_phone || ''}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.client_phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="(11) 99999-9999"
                  />
                  {errors.client_phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.client_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="client_email"
                    value={formData.client_email || ''}
                    onChange={handleInputChange}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.client_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="cliente@email.com"
                  />
                  {errors.client_email && (
                    <p className="text-red-500 text-sm mt-1">{errors.client_email}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço Completo *
                  </label>
                  <textarea
                    name="client_address"
                    value={formData.client_address || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.client_address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Endereço completo com CEP"
                  />
                  {errors.client_address && (
                    <p className="text-red-500 text-sm mt-1">{errors.client_address}</p>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* STEP 2: Técnico */}
            {!isRPEditing3D && currentStep === 2 && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Levantamento Técnico</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição do Ambiente
                  </label>
                  <textarea
                    name="environment_description"
                    value={formData.environment_description || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Descreva o ambiente: tamanho, layout, características..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações Técnicas
                  </label>
                  <textarea
                    name="technical_observations"
                    value={formData.technical_observations || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Limitações, exigências, tipo de parede, corredor estreito..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Informações de Encanamento
                  </label>
                  <textarea
                    name="plumbing_info"
                    value={formData.plumbing_info || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Onde passa o encanamento, pontos de água, gás..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utilidade dos Móveis
                  </label>
                  <textarea
                    name="furniture_purpose"
                    value={formData.furniture_purpose || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Para que serve cada móvel, o que vai guardar, medidas específicas..."
                  />
                </div>
              </div>
            </div>
            )}

            {/* STEP 3: Materiais */}
            {!isRPEditing3D && currentStep === 3 && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">📊 Preferências de Material (para Precificação)</h3>
                {!materialNotCollected && (
                  <button
                    type="button"
                    onClick={handleMaterialNotCollected}
                    className="px-3 py-1.5 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg text-sm hover:bg-orange-200 transition flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
                    Informações não coletadas
                  </button>
                )}
                {materialNotCollected && (
                  <span className="px-3 py-1.5 bg-gray-100 text-gray-600 border border-gray-300 rounded-lg text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    Informações não coletadas
                  </span>
                )}
              </div>
              <div className="space-y-4">
                {/* Tipo de Material */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${materialNotCollected ? 'text-gray-400' : 'text-gray-700'}`}>
                    Tipo de Material que o Cliente vai Querer
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[1, 2, 3].map((type) => (
                      <label key={type} className={`flex items-center ${materialNotCollected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={materialNotCollected}
                          checked={(formData.material_preferences.material_type || []).includes(String(type))}
                          onChange={(e) => {
                            const current = formData.material_preferences.material_type || [];
                            const newValue = e.target.checked
                              ? [...current, String(type)]
                              : current.filter(t => t !== String(type));
                            setFormData(prev => ({
                              ...prev,
                              material_preferences: {
                                ...prev.material_preferences,
                                material_type: newValue
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span className="ml-2 text-sm text-gray-700">Tipo {type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Tipo de Ferragens */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${materialNotCollected ? 'text-gray-400' : 'text-gray-700'}`}>
                    Tipo de Ferragens que o Cliente vai Querer
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[1, 2, 3].map((type) => (
                      <label key={type} className={`flex items-center ${materialNotCollected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={materialNotCollected}
                          checked={(formData.material_preferences.hardware_type || []).includes(String(type))}
                          onChange={(e) => {
                            const current = formData.material_preferences.hardware_type || [];
                            const newValue = e.target.checked
                              ? [...current, String(type)]
                              : current.filter(t => t !== String(type));
                            setFormData(prev => ({
                              ...prev,
                              material_preferences: {
                                ...prev.material_preferences,
                                hardware_type: newValue
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span className="ml-2 text-sm text-gray-700">Tipo {type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Com Vidro ou Não */}
                <div>
                  <label className={`flex items-center ${materialNotCollected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      disabled={materialNotCollected}
                      checked={formData.material_preferences.has_glass}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          material_preferences: {
                            ...prev.material_preferences,
                            has_glass: e.target.checked
                          }
                        }));
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Vai querer com vidro</span>
                  </label>
                </div>

                {/* Tipo de Acabamento para Portas */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${materialNotCollected ? 'text-gray-400' : 'text-gray-700'}`}>
                    Tipo de Acabamento para Portas
                  </label>
                  <div className="space-y-2">
                    {['Liso', 'Texturizado', 'Lacado', 'Verniz', 'Outro'].map((finish) => (
                      <label key={finish} className={`flex items-center ${materialNotCollected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          disabled={materialNotCollected}
                          checked={(formData.material_preferences.door_finish || []).includes(finish)}
                          onChange={(e) => {
                            const current = formData.material_preferences.door_finish || [];
                            const newValue = e.target.checked
                              ? [...current, finish]
                              : current.filter(f => f !== finish);
                            setFormData(prev => ({
                              ...prev,
                              material_preferences: {
                                ...prev.material_preferences,
                                door_finish: newValue
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span className="ml-2 text-sm text-gray-700">{finish}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* STEP 4: Arquivos */}
            {!isRPEditing3D && currentStep === 4 && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-green-600" />
                📱 Arquivos do Levantamento
              </h3>
              <div className="space-y-3">
                <div className="bg-white p-3 rounded border">
                  <h4 className="font-medium text-gray-800 mb-2">🎯 O que enviar:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>📱 Arquivos do Tablet:</strong> Plantas, medidas, anotações digitais</li>
                    <li>• <strong>📸 Fotos do Ambiente:</strong> Visão geral, detalhes importantes</li>
                    <li>• <strong>📷 Limitações:</strong> Drywall, corredores estreitos, obstáculos</li>
                    <li>• <strong>🔧 Instalações:</strong> Encanamento, elétrica, pontos existentes</li>
                    <li>• <strong>📄 Documentos:</strong> Projetos existentes, especificações</li>
                  </ul>
                </div>
                
                <FileUpload
                  surveyId={savedSurveyId || survey?.id}
                  existingFiles={files}
                  onFilesChange={setFiles}
                  disabled={false}
                />
              </div>
            </div>
            )}

            {/* STEP 5: Revisão */}
            {!isRPEditing3D && currentStep === 5 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">📋 Revisão do Levantamento</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-medium text-gray-900 mb-2">Dados do Cliente</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div><strong>Nome:</strong> {formData.client_name || '-'}</div>
                    <div><strong>Telefone:</strong> {formData.client_phone || '-'}</div>
                    <div><strong>Email:</strong> {formData.client_email || '-'}</div>
                    <div className="col-span-2"><strong>Endereço:</strong> {formData.client_address || '-'}</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-medium text-gray-900 mb-2">Levantamento Técnico</h4>
                  <div className="text-sm space-y-1">
                    <div><strong>Descrição:</strong> {formData.environment_description || '-'}</div>
                    <div><strong>Observações:</strong> {formData.technical_observations || '-'}</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-medium text-gray-900 mb-2">Arquivos</h4>
                  <div className="text-sm">
                    <div><strong>Quantidade:</strong> {files.length}</div>
                  </div>
                </div>
                {/* Validação de Croquis 2D */}
                {(user.role === 'GERENCIA' || user.role === 'RECEPCAO_PROJETOS') && savedSurveyId && (
                  <SketchValidationPanel surveyId={savedSurveyId || survey?.id} />
                )}
              </div>
            </div>
            )}

            {/* Navegação do Wizard */}
            {!isRPEditing3D && (
              <div className="flex justify-between gap-3">
                <button type="button" onClick={() => setCurrentStep(s => Math.max(1, s - 1))} disabled={currentStep === 1} className="px-4 sm:px-6 py-3 sm:py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Anterior</span>
                </button>
                {currentStep < 5 && (
                  <button type="button" onClick={handleNextStep} className="px-4 sm:px-6 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    {currentStep === 4 ? 'Ir para Revisão' : 'Próximo'} <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

          {/* Projeto 3D - Mostrar quando projeto 3D foi iniciado (não mostrar se RP está editando 3D) */}
          {!isRPEditing3D && (user.role === 'GERENCIA' || user.role === 'RECEPCAO_PROJETOS') && project3DStatus !== 'PENDING' && savedSurveyId && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Box className="w-5 h-5 text-purple-600" />
                🎨 Projeto 3D
              </h3>
              <div className="space-y-4">
                {/* Status do Projeto 3D */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status do Projeto 3D
                    </label>
                    <select
                      value={project3DStatus}
                      onChange={(e) => setProject3DStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {surveyService.get3DStatusOptions(user?.role).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Observações do Projeto 3D */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações do Projeto 3D
                  </label>
                  <textarea
                    value={project3DObservations}
                    onChange={(e) => setProject3DObservations(e.target.value)}
                    rows={6}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Descreva o projeto 3D, observações técnicas, ajustes necessários, especificações importantes..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use este campo para documentar detalhes do projeto 3D, ajustes necessários, especificações técnicas, etc.
                  </p>
                </div>

                {/* Upload de Arquivos 3D */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Arquivos do Projeto 3D
                  </h4>

                  {/* Lista de arquivos 3D existentes */}
                  {files3D.length > 0 && (
                    <div className="mb-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Arquivos enviados:</p>
                      <div className="space-y-2">
                        {files3D.map((file) => (
                          <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                            <div className="flex items-center gap-2 flex-1">
                              <FileText className="w-4 h-4 text-purple-600" />
                              <span className="text-sm text-gray-700 truncate">{file.original_name}</span>
                              <span className="text-xs text-gray-500">
                                ({surveyService.formatFileSize(file.file_size)})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const token = localStorage.getItem('accessToken');
                                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                                const apiPath = baseUrl.includes('/api') ? '' : '/api';
                                window.location.href = `${baseUrl}${apiPath}/surveys/files/${file.id}/download?token=${token}`;
                              }}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Baixar arquivo"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await surveyService.deleteFile(file.id);
                                  setFiles3D(files3D.filter(f => f.id !== file.id));
                                  toast.success('Arquivo removido com sucesso');
                                } catch (error) {
                                  toast.error('Erro ao remover arquivo');
                                }
                              }}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remover arquivo"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload de novos arquivos */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      accept=".3ds,.max,.blend,.obj,.fbx,.dae,.ply,.stl,.gltf,.glb,.skp,.dwg,.dxf,.pdf,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const selectedFiles = Array.from(e.target.files);
                        if (selectedFiles.length === 0) return;

                        try {
                          setUploading3D(true);
                          const response = await surveyService.upload3DFiles(
                            savedSurveyId,
                            selectedFiles,
                            'Arquivos do projeto 3D'
                          );

                          setFiles3D([...files3D, ...response.data]);
                          toast.success(`${selectedFiles.length} arquivo(s) 3D enviado(s) com sucesso!`);

                          // Limpar input
                          e.target.value = '';
                        } catch (error) {
                          console.error('Erro ao fazer upload:', error);
                          toast.error('Erro ao fazer upload dos arquivos 3D');
                        } finally {
                          setUploading3D(false);
                        }
                      }}
                      disabled={uploading3D || !savedSurveyId}
                      className="hidden"
                      id="upload-3d-files"
                    />
                    <label
                      htmlFor="upload-3d-files"
                      className={`flex flex-col items-center justify-center cursor-pointer ${
                        uploading3D || !savedSurveyId ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Upload className="w-8 h-8 text-purple-600 mb-2" />
                      <p className="text-sm text-gray-700 font-medium">
                        {uploading3D ? 'Enviando...' : 'Clique para selecionar arquivos 3D'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Formatos: .3ds, .max, .blend, .obj, .fbx, .dae, .ply, .stl, .gltf, .glb, .skp, .dwg, .dxf, .pdf, imagens
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Máximo 20 arquivos, 50MB cada
                      </p>
                    </label>
                  </div>
                </div>

                {/* Seção de Template 3D - Apenas para GERENCIA */}
                {user.role === 'GERENCIA' && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <Box className="w-4 h-4" />
                      Template 3D (Pré-visualização)
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Faça upload do template 3D pronto para este levantamento. Esta opção está disponível independentemente do status do levantamento.
                    </p>

                    {/* Upload de novo template */}
                    <div className="border-2 border-dashed border-indigo-300 rounded-lg p-4">
                      <input
                        type="file"
                        multiple
                        accept=".3ds,.max,.blend,.obj,.fbx,.dae,.ply,.stl,.gltf,.glb,.skp,.dwg,.dxf,.pdf,.jpg,.jpeg,.png"
                        onChange={async (e) => {
                          const selectedFiles = Array.from(e.target.files);
                          if (selectedFiles.length === 0) return;

                          try {
                            setUploading3D(true);
                            const response = await surveyService.uploadTemplate3D(
                              savedSurveyId,
                              selectedFiles,
                              'Template 3D - Pré-visualização'
                            );

                            // Atualizar a lista de arquivos 3D também
                            setFiles3D([...files3D, ...response.data]);
                            toast.success(`${selectedFiles.length} template(s) 3D enviado(s) com sucesso!`);

                            // Limpar input
                            e.target.value = '';
                          } catch (error) {
                            console.error('Erro ao fazer upload do template:', error);
                            toast.error('Erro ao fazer upload do template 3D');
                          } finally {
                            setUploading3D(false);
                          }
                        }}
                        disabled={uploading3D || !savedSurveyId}
                        className="hidden"
                        id="upload-template-3d"
                      />
                      <label
                        htmlFor="upload-template-3d"
                        className={`flex flex-col items-center justify-center cursor-pointer ${
                          uploading3D || !savedSurveyId ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Box className="w-8 h-8 text-indigo-600 mb-2" />
                        <p className="text-sm text-gray-700 font-medium">
                          {uploading3D ? 'Enviando...' : 'Clique para selecionar template 3D'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Formatos: .3ds, .max, .blend, .obj, .fbx, .dae, .ply, .stl, .gltf, .glb
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Este upload está disponível para a gerência a qualquer momento
                        </p>
                      </label>
                    </div>
                  </div>
                )}

                {/* Botões de Aprovação/Rejeição - Apenas para GERENCIA quando status = COMPLETED */}
                {user.role === 'GERENCIA' && project3DStatus === 'COMPLETED' && (
                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowRejectModal(true)}
                      disabled={rejecting3D || approving3D}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      {rejecting3D ? 'Rejeitando...' : 'Rejeitar 3D'}
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setApproving3D(true);
                          await projectService.approve3DProject(savedSurveyId);
                          toast.success('Projeto 3D aprovado! Projeto criado com sucesso.');
                          if (onSuccess) onSuccess();
                          if (queryClient) queryClient.invalidateQueries(['surveys']);
                        } catch (error) {
                          toast.error(error?.response?.data?.message || 'Erro ao aprovar projeto 3D');
                        } finally {
                          setApproving3D(false);
                        }
                      }}
                      disabled={rejecting3D || approving3D}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {approving3D ? 'Aprovando...' : 'Aprovar 3D'}
                    </button>
                  </div>
                )}

              </div>
            </div>
          )}

          </div>

          {/* Footer - Não mostrar quando RP está editando 3D (já tem botões na seção 3D) */}
          {!isRPEditing3D && (
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 sm:px-6 py-3 sm:py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              onClick={() => {
                if (savedSurveyId && !isEditing) {
                  setShouldFinalize(true);
                }
              }}
              className="px-4 sm:px-6 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : (
                isEditing ? 'Atualizar' : (
                  savedSurveyId ? (
                    <>
                      <span className="hidden sm:inline">Salvar e Fechar</span>
                      <span className="sm:hidden">Salvar</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Criar Levantamento</span>
                      <span className="sm:hidden">Criar</span>
                    </>
                  )
                )
              )}
            </button>
          </div>
          )}

          {/* Modal para criar cliente rapidamente */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Novo Cliente</h2>
              <button
                onClick={() => {
                  setShowClientModal(false);
                  setNewClientData({
                    full_name: '',
                    email: '',
                    phone: '',
                    document_id: '',
                    address: ''
                  });
                  setNewClientAddressFields({
                    cep: '',
                    street: '',
                    number: '',
                    complement: '',
                    neighborhood: '',
                    city: '',
                    state: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!newClientData.full_name.trim()) {
                  toast.error('Nome do cliente é obrigatório');
                  return;
                }
                if (!newClientData.apelido.trim()) {
                  toast.error('Apelido é obrigatório');
                  return;
                }
                // Construir endereço completo
                const fullAddress = buildNewClientFullAddress();
                const submitData = {
                  ...newClientData,
                  address: fullAddress || newClientData.address
                };
                createClientMutation.mutate(submitData);
              }}
              className="p-6 space-y-4 max-h-[80vh] overflow-y-auto"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={newClientData.full_name}
                  onChange={(e) => setNewClientData({ ...newClientData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Nome completo do cliente"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apelido *
                </label>
                <input
                  type="text"
                  value={newClientData.apelido}
                  onChange={(e) => setNewClientData({ ...newClientData, apelido: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Como o cliente será chamado"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  type="text"
                  value={newClientData.phone}
                  onChange={(e) => {
                    const masked = maskPhone(e.target.value);
                    setNewClientData({ ...newClientData, phone: masked });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  value={newClientData.document_id}
                  onChange={(e) => {
                    const masked = maskDocument(e.target.value);
                    setNewClientData({ ...newClientData, document_id: masked });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength={18}
                />
              </div>

              {/* CEP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newClientAddressFields.cep}
                    onChange={handleCepChangeNewClient}
                    onBlur={handleCepBlurNewClient}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="00000-000"
                    maxLength={9}
                  />
                  <button
                    type="button"
                    onClick={() => fetchCepNewClient(newClientAddressFields.cep)}
                    disabled={loadingCepNewClient || newClientAddressFields.cep.replace(/\D/g, '').length !== 8}
                    className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingCepNewClient ? '...' : 'Buscar'}
                  </button>
                </div>
              </div>

              {/* Rua */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rua/Logradouro
                </label>
                <input
                  type="text"
                  value={newClientAddressFields.street}
                  onChange={(e) => handleAddressFieldChangeNewClient('street', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Nome da rua, avenida, etc."
                />
              </div>

              {/* Número e Complemento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    value={newClientAddressFields.number}
                    onChange={(e) => handleAddressFieldChangeNewClient('number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={newClientAddressFields.complement}
                    onChange={(e) => handleAddressFieldChangeNewClient('complement', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Apto, Bloco, etc."
                  />
                </div>
              </div>

              {/* Bairro */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro
                </label>
                <input
                  type="text"
                  value={newClientAddressFields.neighborhood}
                  onChange={(e) => handleAddressFieldChangeNewClient('neighborhood', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  placeholder="Nome do bairro"
                />
              </div>

              {/* Cidade e Estado */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={newClientAddressFields.city}
                    onChange={(e) => handleAddressFieldChangeNewClient('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                    placeholder="Nome da cidade"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado (UF)
                  </label>
                  <select
                    value={newClientAddressFields.state}
                    onChange={(e) => handleAddressFieldChangeNewClient('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent"
                  >
                    <option value="">Selecione</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowClientModal(false);
                    setNewClientData({
                      full_name: '',
                      apelido: '',
                      email: '',
                      phone: '',
                      document_id: '',
                      address: ''
                    });
                    setNewClientAddressFields({
                      cep: '',
                      street: '',
                      number: '',
                      complement: '',
                      neighborhood: '',
                      city: '',
                      state: ''
                    });
                  }}
                  className="px-4 sm:px-6 py-3 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createClientMutation.isLoading}
                  className="px-4 sm:px-6 py-3 sm:py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {createClientMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Criando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Criar Cliente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para rejeitar projeto 3D */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-red-600">Rejeitar Projeto 3D</h2>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!rejectionReason.trim()) {
                  toast.error('Por favor, informe o motivo da rejeição');
                  return;
                }
                handleReject3D();
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo da Rejeição *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Descreva o motivo pelo qual o projeto 3D está sendo rejeitado. Este motivo será enviado para a equipe de projetos."
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  O levantamento retornará para o status "EM_PROJETACAO" e a equipe de RECEPCAO_PROJETOS será notificada.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={rejecting3D}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {rejecting3D ? 'Rejeitando...' : 'Confirmar Rejeição'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </form>
    </div>
    </div>
  );
};

// Componente de validação de croquis na aba Revisão
const SketchValidationPanel = ({ surveyId }) => {
  const { data: environmentsData, isLoading } = useQuery({
    queryKey: ['survey-environments', surveyId],
    queryFn: () => surveyService.getEnvironments(surveyId),
    enabled: !!surveyId
  });

  const environments = environmentsData?.environments || [];
  const totalEnvironments = environments.length;
  const environmentsWithSketch = environments.filter(e => e.sketch_project_id).length;
  const allSketchesComplete = totalEnvironments > 0 && environmentsWithSketch === totalEnvironments;

  if (isLoading) {
    return (
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium text-gray-900 mb-2">Croquis 2D</h4>
        <p className="text-sm text-gray-500">Carregando...</p>
      </div>
    );
  }

  if (totalEnvironments === 0) {
    return (
      <div className="bg-white p-4 rounded-lg border border-yellow-200">
        <div className="flex items-center gap-2 text-yellow-700">
          <AlertTriangle className="w-5 h-5" />
          <h4 className="font-medium">Croquis 2D</h4>
        </div>
        <p className="text-sm text-yellow-600 mt-2">
          Nenhum ambiente cadastrado ainda. Vá para a aba "Ambientes" para criar os ambientes e adicionar os croquis 2D.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white p-4 rounded-lg border ${allSketchesComplete ? 'border-green-200' : 'border-red-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layout className="w-5 h-5 text-gray-700" />
          <h4 className="font-medium text-gray-900">Croquis 2D</h4>
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          allSketchesComplete
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`}>
          {allSketchesComplete ? (
            <>
              <CheckCircle className="w-3 h-3" />
              <span>Completo</span>
            </>
          ) : (
            <>
              <AlertTriangle className="w-3 h-3" />
              <span>Incompleto</span>
            </>
          )}
        </div>
      </div>

      {/* Progresso geral */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{environmentsWithSketch} de {totalEnvironments} ambientes com croqui</span>
          <span>{totalEnvironments > 0 ? Math.round((environmentsWithSketch / totalEnvironments) * 100) : 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${allSketchesComplete ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${totalEnvironments > 0 ? (environmentsWithSketch / totalEnvironments) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Lista de ambientes */}
      <div className="space-y-2">
        {environments.map((env) => (
          <div key={env.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                env.sketch_project_id
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}>
                {env.sketch_project_id ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <X className="w-3 h-3" />
                )}
              </span>
              <div>
                <div className="text-sm font-medium text-gray-900">{env.environment_name || env.name || `Ambiente ${env.id}`}</div>
                <div className="text-xs text-gray-500">{env.environment_type || env.room_type || 'Não definido'}</div>
              </div>
            </div>
            <div className={`text-xs px-2 py-1 rounded ${
              env.sketch_project_id
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {env.sketch_project_id ? 'Croqui OK' : 'Sem croqui'}
            </div>
          </div>
        ))}
      </div>

      {/* Aviso se incompleto */}
      {!allSketchesComplete && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Croquis incompletos</p>
              <p className="text-red-600 mt-1">
                Todos os ambientes devem ter croqui 2D antes de finalizar o levantamento.
                Vá para a aba "Ambientes" e adicione o croqui de cada ambiente pendente.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyForm;
